/* @jsx h */
import { h, createContext } from "preact";
import { useReducer, useContext, useEffect, useCallback } from "preact/hooks";
import { reducer, initialState } from "./reducer";
import { checklistActions, checklistSyncError, localStorageKeys } from "../constants";
import { getCampaignData, getUserCheckedItems as getUserCheckedItemsFromDB, putUserCheckedItems } from "../api";
import { updateStorage, getUserCheckedItemsFromStorage, parseCheckedItems, getToken } from "../helpers";
import PropTypes from "prop-types";

/*
 * I chose to use a context provider to avoid prop drilling state values like userCheckedItems
 * several nested components deep. Since the amount of state data we're using now is so tiny (~80 list items)
 * I considered a redux/mobx store to be overkill (until performance tests suggest otherwise).
 *
 * For an overview of application architecture, data and data flow, view the confluence documentation here:
 * https://confluence.build.ingka.ikea.com/display/USDHCXMF/Campaign+Checklist#CampaignChecklist-Architecture
 */

/*
 * 1. User lands on Checklist.
 *
 * 2. Client fetches campaign-specific data and checks if user is logged in
 * (by calling getToken).
 *
 * 3. If a user is logged in, the database is the source of truth for data. Client queries database
 * for checklist data.
 *
 * 3a. If 200/content is returned, state is synchronized to the returned response.
 *
 * 3b. If a 204 is returned, the database has successfully been queried, but the user has not previously
 * saved data. Client checks localstorage.userCheckedItems- if value exists, state is synchronized to
 * local storage, and a POST request is sent to synchronize value in database.
 *
 * 3c. If a 204 is returned, and no value is found in localstorage.userCheckedItems, this means a logged in
 * user has landed on the checklist for the first time.  client sets userCheckedItems
 * in state to an empty set (for O(1) lookup).
 *
 * 4. If the user is not logged in, localstorage is the source of truth for data.
 * Client checks if localStorage.userCheckedItems exists. If checked items exist in storage,
 * synchronize value in state to match local storage.
 *
 * 5. If the user is not logged in, and nothing is in local storage,
 * client sets userCheckedItems in state to an empty set (for O(1) lookup).
 *
 * 8. Client generates notification/save button depending on whether user is authenticated.
 *
 * 9. On check or uncheck, update checkedItems in state.
 * If user is logged in, update database, otherwise update local storage.
 *
 * To avoid frequent pings to server, debounce updates (batch updates and send after timeout of a few seconds).
 */

const ChecklistContext = createContext();
export const useChecklistContext = () => useContext(ChecklistContext);

const ChecklistProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const {
        sublists,
        userCheckedItems,
        userClosedSublists,
        title,
        subtitle,
        userToken,
        error,
        autosaveError,
        loaded,
        forcedLoggedOut
    } = state;

    /**
     * Called to retrieve user checked items + status of request:
     *      1. If no id, user checked items are sourced from local storage.
     *      2. Else, user checked items are sourced from the database
     *      3. If there is a userid (from the token), but the database does not contain checked items for that user,
     * items are sourced from local storage.
     * @param {string | null} accessToken of signed in user or null
     * @returns {object} contains a status and a data or error property
     */
    const getUserCheckedItems = async (accessToken) => {
        if (!accessToken) return { data: getUserCheckedItemsFromStorage(), status: 200 };
        const { status, body, error } = await getUserCheckedItemsFromDB(accessToken);
        if (error) {
            console.error("Can't retrieve user checklist from database");
            return { error };
        }
        if (status === 204) return { data: getUserCheckedItemsFromStorage(), status: 204 };
        else return { data: body.user_checked_items ? parseCheckedItems(body.user_checked_items) : new Set() };
    };

    /**
     * Makes these changes to the UI upon user sign in:
     *      1. Sets catastrophic error if server is down and user cannot sign in
     *      2. Sets user id/replaces CTA button with Autosave
     *      3. Updates checklist with checked items
     *      4. On first log in, saves locally-stored items and sets autosave error if cannot save items
     *
     * @param {string | null} access token of signed-in user or null, for guest user
     * @returns {object} contains a status and a data or error property
     */
    const updateUIonUserSignIn = useCallback(
        async (accessToken) => {
            const { status, data, error } = await getUserCheckedItems(accessToken);

            //don't display any checklist if there is an error getting a signed-in user's data
            if (error) {
                return dispatch({ type: checklistActions.SET_CHECKLIST_ERROR, payload: error });
            }

            if (accessToken) dispatch({ type: checklistActions.SET_USER_TOKEN, payload: accessToken });

            // update state with either data from local or from database
            dispatch({
                type: checklistActions.SET_USER_CHECKED_ITEMS,
                payload: data
            });

            // on first time log in, display data from local ...
            if (status === 204) {
                // ... and sync database with local string or empty string if nothing exists in local
                const localString = !localStorageKeys.BTC_LOCAL_STORAGE_KEY
                    ? ""
                    : localStorageKeys.BTC_LOCAL_STORAGE_KEY;

                putUserCheckedItems(accessToken, localStorage.getItem(localString)).catch(() => {
                    // @todo potentially set catastrophic error (500) here,
                    // if server is completely down and we don't want
                    // user to access checklist at all?
                    // otherwise, set autosave error- when user checks an item, save will try again
                    setAutosaveError(checklistSyncError);
                });
            }
        },
        [getUserCheckedItems, checklistActions, dispatch, putUserCheckedItems, setAutosaveError]
    );

    // listen for user log out and refresh page if logged out
    // this eliminates opportunities for odd behaviour
    // if a user has many checklists open and logs out of one (+ access token has not yet expired)
    useEffect(() => {
        const watchCookiesForLogOut = async (interval) => {
            const accessToken = document.cookie.match("(^|;)\\s*idp_reguser\\s*=\\s*([^;]+)")?.pop() || "";
            // user logged out
            if (!accessToken) {
                clearInterval(interval);
                window.location.reload();
            }
            // user didn't log out, but access token was refreshed on another page
            else if (accessToken !== userToken) {
                dispatch({ type: checklistActions.SET_USER_TOKEN, payload: accessToken });
            }
        };
        const interval = userToken ? setInterval(() => watchCookiesForLogOut(interval), 1000) : null;
        // clean up
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [userToken]);

    useEffect(() => {
        getCampaignData()
            .then((response) => {
                dispatch({ type: checklistActions.SET_CAMPAIGN_DATA, payload: response });
            })
            .catch((error) => {
                console.error("Can't retrieve campaign data");
                dispatch({ type: checklistActions.SET_CHECKLIST_ERROR, payload: error });
            });

        getToken()
            .then(async ({ accessToken }) => {
                await updateUIonUserSignIn(accessToken);
            })
            .catch((error) => {
                console.error("Can't retrieve user token");
                dispatch({ type: checklistActions.SET_CHECKLIST_ERROR, payload: error });
            });
    }, []);

    const onItemCheck = (sublistId, itemId) => {
        if (!userToken) updateStorage(userCheckedItems, `${sublistId}-${itemId}`);
        // autosave component handles if user is signed in- updates are debounced,
        // so no need to send off save from here.
        dispatch({ type: checklistActions.TOGGLE_CHECK_ITEM, payload: { sublistId, itemId } });
    };

    const onSublistChevronClick = (sublistId) => {
        dispatch({ type: checklistActions.TOGGLE_SUBLIST_CLOSED_STATE, payload: sublistId });
    };

    const setAutosaveError = (error) => {
        dispatch({ type: checklistActions.SET_AUTOSAVE_ERROR, payload: error });
    };

    const setChecklistError = (error) => {
        dispatch({ type: checklistActions.SET_CHECKLIST_ERROR, payload: error });
    };

    const logOutUser = () => {
        // if the user has not signed out but token expired, and then tries to save checklist,
        // reload the window to force the checklist to be signed out
        window.location.reload();
    };

    return (
        <ChecklistContext.Provider
            value={{
                autosaveError,
                title: title,
                subtitle: subtitle,
                sublists: sublists,
                userCheckedItems: userCheckedItems,
                userClosedSublists: userClosedSublists,
                userToken: userToken,
                error: error,
                loaded: loaded,
                forcedLoggedOut: forcedLoggedOut,
                onItemCheck: onItemCheck,
                onSublistChevronClick: onSublistChevronClick,
                setAutosaveError: setAutosaveError,
                logOutUser: logOutUser,
                setChecklistError: setChecklistError
            }}
        >
            {children}
        </ChecklistContext.Provider>
    );
};

export default ChecklistProvider;

ChecklistProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};
